import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Button, Card, Spinner, ProgressBar } from "react-bootstrap";
import Flex from "components/common/Flex";
import cloudUpload from "assets/img/icons/cloud-upload.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faEye } from "@fortawesome/free-solid-svg-icons";

import { useDispatch } from "react-redux";
import IconButton from "components/common/IconButton";
import { Trash } from "react-bootstrap-icons";
import { useLocation } from "react-router-dom";
import { handleFillStepData } from "features/cliams/config/claimSlice";

import {
  compressPDF,
  toBase64,
  compressImage,
  formatFileSize,
} from "../../utils.js";

const DocumentUpload = ({
  setValue,
  clearErrors,
  setError,
  isDisabled = false,
  watch, // Add watch
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isViewRequest = location.pathname.includes("view-claim-request");

  const [files, setFiles] = useState([]);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  // Sync files from react-hook-form state
  useEffect(() => {
    const processAttachments = (attachments) => {
      if (!attachments || !Array.isArray(attachments)) return [];

      return attachments.map((att) => {
        const item = att.valueAttachment || att;
        return {
          originalFile: { name: item.originalName || item.title, size: 0 },
          compressedFile: { size: 0, type: item.contentType },
          preview: (item.contentType && item.contentType.includes("pdf"))
            ? null
            : (item.url || (item.data ? `data:${item.contentType};base64,${item.data}` : null)),
          type: item.contentType,
          path: item.title,
          base64: item.data,
          title: item.title,
          creation: item.creation,
          url: item.url
        };
      });
    };

    const subscription = watch((value, { name }) => {
      if (name === "attachments") {
        setFiles((prev) => {
          const newFiles = processAttachments(value.attachments);
          if (prev.length === newFiles.length) {
            const same = prev.every((p, i) => p.path === newFiles[i].path);
            if (same) return prev;
          }
          return newFiles;
        });


      }
    });

    const currentAttachments = watch("attachments");
    if (currentAttachments && Array.isArray(currentAttachments) && currentAttachments.length > 0) {
      setFiles(processAttachments(currentAttachments));
    }

    return () => subscription.unsubscribe();
  }, [watch]);


  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".png", ".jpg"],
      "application/pdf": [".pdf"],
    },
    onDrop: async (acceptedFiles) => {
      setCompressionProgress(10);
      setUploadError(null);

      try {
        const processedFiles = await Promise.all(
          acceptedFiles.map(async (originalFile, index) => {
            let compressedFile = originalFile;

            if (originalFile.type === "application/pdf") {
              setCompressionProgress(40);
              compressedFile = await compressPDF(originalFile);
              setCompressionProgress(80);
            } else if (originalFile.type.startsWith("image/")) {
              compressedFile = await compressImage(originalFile);
            }

            // Check compressed file size (10MB limit)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (compressedFile.size > maxSize) {
              throw new Error(
                `Compressed file "${originalFile.name
                }" exceeds 10MB limit (${formatFileSize(compressedFile.size)})`
              );
            }

            const base64Data = await toBase64(compressedFile);

            const ext = originalFile.name
              .substring(originalFile.name.lastIndexOf("."))
              .toLowerCase();
            const creation = new Date().toISOString().split("T")[0];
            const hash = `${Date.now()}-${index}`;
            const title = `${hash}${ext}`;

            return {
              originalFile,
              compressedFile,
              preview: compressedFile.type.includes("pdf")
                ? null
                : URL.createObjectURL(compressedFile),
              type: compressedFile.type,
              path: title,
              base64: base64Data,
              title,
              creation,
            };
          })
        );

        setFiles((prev) => {
          const updatedFiles = [...prev, ...processedFiles];
          const attachments = updatedFiles.map((f) => ({
            valueAttachment: {
              contentType: f.compressedFile.type,
              title: f.title,
              creation: f.creation,
              data: f.base64,
            },
          }));
          setValue("attachments", attachments);
          return updatedFiles;
        });
        clearErrors("attachments");
        setCompressionProgress(100);
      } catch (error) {
        console.error("Compression error:", error);
        setUploadError(error.message || "Error processing files");
        setError("attachments", {
          type: "manual",
          message: error.message || "Error processing files",
        });
        setCompressionProgress(0);
      }
    },
    onDropRejected: (rejectedFiles) => {
      const errorMessage = rejectedFiles
        .map((file) =>
          file.errors
            .map((err) =>
              err.code === "file-invalid-type"
                ? `Invalid file type for "${file.file.name}"`
                : `File "${file.file.name}" is too large`
            )
            .join(", ")
        )
        .join("; ");
      setUploadError(errorMessage);
      setError("attachments", {
        type: "manual",
        message: errorMessage,
      });
      setCompressionProgress(0);
    },
    // Remove maxSize restriction here to allow larger files before compression
  });

  const handleView = (file) => {
    const dataUrl = `data:${file.type};base64,${file.base64}`;
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(`
        <html>
          <body style="margin:0">
            ${file.type.includes("pdf")
          ? `<embed src="${dataUrl}" type="${file.type}" width="100%" height="100%" />`
          : `<img src="${dataUrl}" style="width:100%;height:auto;" />`
        }
          </body>
        </html>
      `);
      newWindow.document.title = file.title || "Preview";
    }
  };


  console.log(watch("attachments")?.length)

  const handleRemove = (path) => {
    console.log({ path });
    setFiles((prev) => {
      const newFiles = prev.filter((f) => f.path !== path);
      const attachments = newFiles.map((f) => ({
        valueAttachment: {
          contentType: f.type,
          title: f.title,
          creation: f.creation,
          data: f.base64,
        },
      }));
      setValue("attachments", attachments);
      dispatch(handleFillStepData({ check: "attachments", value: newFiles }));
      return newFiles;
    });
  };

  useEffect(() => {
    return () =>
      files.forEach((f) => f.preview && URL.revokeObjectURL(f.preview));
  }, [files]);

  return (
    <Card className="mt-3">
      <Card.Header as="h6" className="bg-body-tertiary">
        Attach Document
      </Card.Header>
      <Card.Body>
        {!isViewRequest && (
          <div {...getRootProps({ className: "dropzone-area py-4" })}>
            <input {...getInputProps({ name: "attachments" })} />
            <div>
              {compressionProgress > 0 && compressionProgress < 100 ? (
                <div className="text-center">
                  <Spinner animation="border" variant="primary" size="sm" />
                  <ProgressBar
                    now={compressionProgress}
                    label={`${compressionProgress}%`}
                    className="mt-2"
                  />
                  <p className="mt-2 mb-0">Compressing files...</p>
                </div>
              ) : (
                <>
                  <img src={cloudUpload} alt="" width={25} className="me-2" />
                  <span>
                    Drag your file here (PDF or Image)
                    <br />
                    or,{" "}
                  </span>
                  <Button variant="link" size="sm">
                    Browse
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
        {uploadError && (
          <div className="text-danger mt-2 fs-10">{uploadError}</div>
        )}

        <div>
          {files.map((file) => {
            const saved =
              file.originalFile.size > 0
                ? (
                  (1 - file.compressedFile.size / file.originalFile.size) *
                  100
                ).toFixed(1)
                : 0;

            return (
              <Flex
                alignItems="center"
                className="py-2 border-bottom"
                key={file.path}
              >
                {file.type.includes("pdf") ? (
                  <FontAwesomeIcon icon={faFilePdf} className="text-danger" />
                ) : (
                  <img
                    className="rounded"
                    width={20}
                    height={20}
                    src={file.preview}
                    alt={file.originalFile.name}
                  />
                )}
                <Flex
                  justifyContent="between"
                  alignItems="center"
                  className="ms-3 flex-1"
                >
                  <div>
                    <h6 className="mb-0">{file.originalFile.name}</h6>

                  </div>
                </Flex>
                {!isDisabled && (
                  <>
                    <IconButton
                      variant="primary"
                      size="sm"
                      onClick={() => handleView(file)}
                      className="ms-2"
                    >
                      <FontAwesomeIcon size={16} icon={faEye} />
                    </IconButton>
                    <IconButton
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemove(file.path)}
                      className="ms-2"
                    >
                      <Trash size={16} />
                    </IconButton>
                  </>
                )}
              </Flex>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default DocumentUpload;
